import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";

/**
 * UI kawaii para cuando el Error Boundary captura un error.
 * Permite: recargar la página, ir al dashboard, reportar el error (preparado para logs).
 * Usa window.location para navegar (funciona aunque el crash sea fuera del Router).
 */
const ErrorBoundaryFallback = ({ error, componentStack, onReportError }) => {
	const [reporting, setReporting] = useState(false);
	const [reported, setReported] = useState(false);

	const handleReload = () => {
		window.location.reload();
	};

	const handleGoDashboard = () => {
		window.location.href = "/";
	};

	const handleReport = async () => {
		if (reported) return;
		setReporting(true);
		try {
			// Cuando estén implementados los logs, aquí se enviaría el error
			if (typeof onReportError === "function") {
				await onReportError({
					message: error?.message,
					stack: error?.stack,
					componentStack: componentStack || null,
					timestamp: new Date().toISOString(),
					userAgent: navigator?.userAgent,
					url: window?.location?.href,
				});
			}
			setReported(true);
		} finally {
			setReporting(false);
		}
	};

	const buttonSx = {
		borderRadius: "14px",
		textTransform: "none",
		fontWeight: 700,
		fontSize: "0.95rem",
		px: 2.5,
		py: 1.25,
		boxShadow: "0 4px 14px rgba(99, 102, 241, 0.25)",
		"&:hover": {
			boxShadow: "0 6px 20px rgba(99, 102, 241, 0.35)",
		},
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "linear-gradient(165deg, #F8FAFC 0%, #EEF2FF 35%, #F5F3FF 70%, #FDF4FF 100%)",
				p: 3,
				boxSizing: "border-box",
			}}
		>
			<Box
				sx={{
					maxWidth: 420,
					width: "100%",
					position: "relative",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					textAlign: "center",
					background: "rgba(255, 255, 255, 0.85)",
					borderRadius: "24px",
					border: "2px solid rgba(99, 102, 241, 0.2)",
					boxShadow:
						"inset 0 2px 24px rgba(99, 102, 241, 0.06), 0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.5)",
					p: 4,
					overflow: "hidden",
				}}
			>
				{/* Decoración de fondo */}
				<Box
					sx={{
						position: "absolute",
						top: -40,
						left: -40,
						width: 120,
						height: 120,
						borderRadius: "50%",
						background: "rgba(168, 85, 247, 0.08)",
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: -30,
						right: -30,
						width: 100,
						height: 100,
						borderRadius: "50%",
						background: "rgba(99, 102, 241, 0.08)",
					}}
				/>

				{/* Icono kawaii: estrella triste con carita + animación floating */}
				<Box
					sx={{
						position: "relative",
						mb: 2,
						"@keyframes float": {
							"0%, 100%": { transform: "translateY(0) rotate(-2deg)" },
							"50%": { transform: "translateY(-10px) rotate(2deg)" },
						},
						animation: "float 3s ease-in-out infinite",
					}}
				>
					<svg
						width="88"
						height="76"
						viewBox="0 0 120 100"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden
					>
						<path
							d="M 53 40.3 Q 60 22 67 40.3 Q 86.6 41.3 71.4 53.8 Q 76.5 72.7 60 62 Q 43.5 72.7 48.6 53.8 Q 33.4 41.3 53 40.3 Z"
							fill="#FACC15"
							stroke="#EAB308"
							strokeWidth="1.5"
							strokeLinejoin="round"
						/>
						<path
							d="M 46 50 Q 50 55 54 50"
							stroke="#1a1a2e"
							strokeWidth="2"
							strokeLinecap="round"
							fill="none"
							style={{ transform: "scale(0.5) translate(59px, 45px)" }}
						/>
						<path
							d="M 66 50 Q 70 55 74 50"
							stroke="#1a1a2e"
							strokeWidth="2"
							strokeLinecap="round"
							fill="none"
							style={{ transform: "scale(0.5) translate(60px, 45px)" }}
						/>
						<path
							d="M 66 50 Q 70 55 74 50"
							stroke="#1a1a2e"
							strokeWidth="2"
							strokeLinecap="round"
							fill="none"
							style={{ transform: "scale(0.5) translate(190px, 156px) rotate(180deg)" }}
						/>
					</svg>
				</Box>

				<Typography
					variant="h1"
					sx={{
						fontSize: "1.75rem",
						fontWeight: 800,
						color: "#4F46E5",
						mb: 0.75,
						letterSpacing: "-0.02em",
					}}
				>
					¡Ups! Algo se ha torcido
				</Typography>
				<Typography
					variant="body2"
					sx={{
						fontSize: "0.95rem",
						color: "#64748B",
						mb: 3,
						lineHeight: 1.5,
					}}
				>
					No te preocupes, no es culpa tuya. Puedes recargar la página, volver al inicio o reportar el error para que lo revisemos.
				</Typography>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1.5,
						width: "100%",
						maxWidth: 280,
					}}
				>
					<Button
						variant="contained"
						onClick={handleReload}
						sx={{
							...buttonSx,
							background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
						}}
					>
						Recargar la página
					</Button>
					<Button
						variant="contained"
						onClick={handleGoDashboard}
						sx={{
							...buttonSx,
							background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
						}}
					>
						Ir al inicio
					</Button>
					<Button
						variant="outlined"
						onClick={handleReport}
						disabled={reporting || reported}
						sx={{
							...buttonSx,
							borderWidth: 2,
							borderColor: "rgba(99, 102, 241, 0.5)",
							color: "#4F46E5",
							"&:hover": {
								borderWidth: 2,
								borderColor: "#6366F1",
								background: "rgba(99, 102, 241, 0.08)",
							},
						}}
					>
						{reported ? "✓ Enviado" : reporting ? "Enviando…" : "Reportar error"}
					</Button>
				</Box>

				{process.env.NODE_ENV === "development" && error?.message && (
					<Box
						sx={{
							mt: 2,
							p: 1.5,
							width: "100%",
							maxHeight: 120,
							overflow: "auto",
							background: "rgba(0,0,0,0.04)",
							borderRadius: "12px",
							textAlign: "left",
						}}
					>
						<Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem", color: "#64748B" }}>
							{error.message}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ErrorBoundaryFallback;
